'use client';
import React, { useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  Cell, ReferenceLine, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis
} from 'recharts';
import { Globe, AlertTriangle, BookOpen } from 'lucide-react';

// IMF/World Bank data — property tax as % of GDP
// Sources: IMF Government Finance Statistics 2023, World Bank BOOST database,
// Lincoln Institute of Land Policy (2022), African Development Bank fiscal data
const BENCHMARK_DATA = [
  {
    country: 'Rwanda',
    flag: '🇷🇼',
    propertyTaxGDP: 0.62,
    totalTaxGDP: 15.8,
    registrationRate: 78,
    digitalSystem: true,
    note: 'RLMUA digital cadastre since 2012. Model for East Africa.',
    color: '#4ade80',
    status: 'Leader',
  },
  {
    country: 'Kenya',
    flag: '🇰🇪',
    propertyTaxGDP: 0.48,
    totalTaxGDP: 14.2,
    registrationRate: 62,
    digitalSystem: true,
    note: 'County-level digital valuation rolls. Nairobi leads at 0.8% GDP.',
    color: '#60a5fa',
    status: 'Strong',
  },
  {
    country: 'Ghana',
    flag: '🇬🇭',
    propertyTaxGDP: 0.31,
    totalTaxGDP: 13.1,
    registrationRate: 45,
    digitalSystem: true,
    note: 'LUSPA digital land registry. Accra Metropolitan Assembly model.',
    color: '#a3e635',
    status: 'Moderate',
  },
  {
    country: 'Senegal',
    flag: '🇸🇳',
    propertyTaxGDP: 0.28,
    totalTaxGDP: 12.4,
    registrationRate: 38,
    digitalSystem: false,
    note: 'Partial digitisation. Urban areas stronger than rural.',
    color: '#fbbf24',
    status: 'Moderate',
  },
  {
    country: 'Côte d\'Ivoire',
    flag: '🇨🇮',
    propertyTaxGDP: 0.22,
    totalTaxGDP: 11.8,
    registrationRate: 32,
    digitalSystem: false,
    note: 'DGI reform underway. Abidjan focus.',
    color: '#fb923c',
    status: 'Developing',
  },
  {
    country: 'Sierra Leone',
    flag: '🇸🇱',
    propertyTaxGDP: 0.18,
    totalTaxGDP: 10.2,
    registrationRate: 22,
    digitalSystem: false,
    note: 'NRA reform in progress. Freetown pilot.',
    color: '#f97316',
    status: 'Developing',
  },
  {
    country: 'Liberia (Current)',
    flag: '🇱🇷',
    propertyTaxGDP: 0.09,
    totalTaxGDP: 9.1,
    registrationRate: 11,
    digitalSystem: false,
    note: 'LRA FY2024: $5.14M collected. 11% registration. Significant upside.',
    color: '#f87171',
    status: 'Critical Gap',
  },
  {
    country: 'Liberia (Target)',
    flag: '🇱🇷',
    propertyTaxGDP: 0.50,
    totalTaxGDP: 12.5,
    registrationRate: 80,
    digitalSystem: true,
    note: 'LRA Nexus full deployment target. IMF Article IV recommended threshold.',
    color: '#d4a017',
    status: 'Target',
  },
];

const RADAR_DATA = [
  { metric: 'Tax/GDP %', Rwanda: 99, Kenya: 77, Ghana: 50, Liberia: 14, LiberiaTarget: 80 },
  { metric: 'Registration', Rwanda: 78, Kenya: 62, Ghana: 45, Liberia: 11, LiberiaTarget: 80 },
  { metric: 'Digital System', Rwanda: 100, Kenya: 100, Ghana: 100, Liberia: 0, LiberiaTarget: 100 },
  { metric: 'Compliance', Rwanda: 72, Kenya: 58, Ghana: 42, Liberia: 15, LiberiaTarget: 75 },
  { metric: 'Collection Eff.', Rwanda: 85, Kenya: 70, Ghana: 55, Liberia: 20, LiberiaTarget: 80 },
];

interface TooltipProps {
  active?: boolean;
  payload?: Array<{ value: number; name: string; color: string }>;
  label?: string;
}

function CustomTooltip({ active, payload, label }: TooltipProps) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl px-3 py-2 shadow-xl text-xs"
      style={{ background: 'var(--dark-elevated)', border: '1px solid var(--dark-border)', minWidth: 160 }}>
      <p className="font-bold mb-1" style={{ color: 'var(--dark-text)' }}>{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }}>Property Tax/GDP: <span className="font-bold">{p.value}%</span></p>
      ))}
    </div>
  );
}

const STATUS_STYLE: Record<string, { bg: string; color: string }> = {
  'Leader': { bg: 'rgba(74,222,128,0.15)', color: '#4ade80' },
  'Strong': { bg: 'rgba(96,165,250,0.15)', color: '#60a5fa' },
  'Moderate': { bg: 'rgba(251,191,36,0.15)', color: '#fbbf24' },
  'Developing': { bg: 'rgba(251,146,60,0.15)', color: '#fb923c' },
  'Critical Gap': { bg: 'rgba(239,68,68,0.15)', color: '#f87171' },
  'Target': { bg: 'rgba(212,160,23,0.15)', color: '#d4a017' },
};

export default function IMFBenchmarks() {
  const [activeView, setActiveView] = useState<'bar' | 'radar' | 'table'>('bar');

  const chartData = BENCHMARK_DATA.map(d => ({
    name: d.country.replace('Liberia (Current)', 'Liberia\n(Now)').replace('Liberia (Target)', 'Liberia\n(Target)').replace('Côte d\'Ivoire', 'Côte d\'Iv.'),
    value: d.propertyTaxGDP,
    color: d.color,
  }));

  return (
    <section className="rounded-2xl overflow-hidden" style={{ border: '1px solid var(--dark-border)', background: 'var(--dark-card)' }}>
      {/* Header */}
      <div className="px-5 pt-5 pb-4" style={{
        borderBottom: '1px solid var(--dark-border)',
        background: 'linear-gradient(135deg, rgba(96,165,250,0.08) 0%, rgba(212,160,23,0.06) 100%)'
      }}>
        <div className="flex items-center gap-3 mb-1">
          <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: 'rgba(96,165,250,0.15)' }}>
            <Globe size={16} style={{ color: '#60a5fa' }} />
          </div>
          <div>
            <span className="text-xs font-semibold tracking-widest uppercase" style={{ color: '#60a5fa' }}>IMF & World Bank Benchmarks</span>
            <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>Property tax performance vs regional peers — 2023 data</p>
          </div>
          <div className="ml-auto flex items-center gap-1.5 px-3 py-1 rounded-full text-xs"
            style={{ background: 'rgba(96,165,250,0.1)', color: '#60a5fa', border: '1px solid rgba(96,165,250,0.2)' }}>
            <BookOpen size={11} />
            IMF GFS 2023 · World Bank BOOST
          </div>
        </div>
        <h2 className="text-lg font-bold mt-2" style={{ color: 'var(--dark-text)' }}>
          Liberia at 0.09% — The Lowest Property Tax/GDP in West Africa
        </h2>
        <p className="text-xs mt-0.5 max-w-2xl" style={{ color: 'var(--dark-muted)' }}>
          Rwanda collects 0.62% of GDP from property tax. Kenya 0.48%. Ghana 0.31%. Liberia collects 0.09% — less than one-sixth of the regional average. LRA Nexus targets 0.50%, aligning with IMF Article IV recommendations.
        </p>

        {/* View toggle */}
        <div className="flex gap-2 mt-3">
          {[
            { key: 'bar', label: 'Bar Chart' },
            { key: 'radar', label: 'Radar Comparison' },
            { key: 'table', label: 'Full Table' },
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setActiveView(key as 'bar' | 'radar' | 'table')}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
              style={{
                background: activeView === key ? 'rgba(96,165,250,0.15)' : 'var(--dark-elevated)',
                color: activeView === key ? '#60a5fa' : 'var(--dark-muted)',
                border: `1px solid ${activeView === key ? 'rgba(96,165,250,0.3)' : 'var(--dark-border)'}`,
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="px-5 py-5">
        {/* Gap callout */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          {[
            { label: 'Liberia Current', value: '0.09%', sub: 'Property Tax / GDP', color: '#f87171' },
            { label: 'Regional Average', value: '0.35%', sub: 'West Africa peer avg', color: '#fbbf24' },
            { label: 'IMF Recommended', value: '0.50%', sub: 'Article IV threshold', color: '#60a5fa' },
            { label: 'LRA Nexus Target', value: '0.50%', sub: 'Achievable in 5 years', color: '#d4a017' },
          ].map(({ label, value, sub, color }) => (
            <div key={label} className="rounded-xl px-4 py-3" style={{ background: 'var(--dark-elevated)', border: '1px solid var(--dark-border)' }}>
              <p className="text-xs mb-1" style={{ color: 'var(--dark-muted)' }}>{label}</p>
              <p className="text-2xl font-black" style={{ color, letterSpacing: '-0.02em' }}>{value}</p>
              <p className="text-xs mt-0.5" style={{ color: 'var(--dark-muted)' }}>{sub}</p>
            </div>
          ))}
        </div>

        {activeView === 'bar' && (
          <div className="rounded-xl p-4" style={{ background: 'var(--dark-elevated)', border: '1px solid var(--dark-border)' }}>
            <p className="text-xs font-semibold mb-3" style={{ color: 'var(--dark-text)' }}>Property Tax as % of GDP — Regional Comparison</p>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <XAxis dataKey="name" tick={{ fontSize: 9, fill: 'var(--dark-muted)' }} axisLine={false} tickLine={false}
                  tickFormatter={v => v.replace('\n', ' ')} />
                <YAxis tick={{ fontSize: 10, fill: 'var(--dark-muted)' }} axisLine={false} tickLine={false}
                  tickFormatter={v => `${v}%`} domain={[0, 0.8]} />
                <Tooltip content={<CustomTooltip />} />
                <ReferenceLine y={0.5} stroke="#d4a017" strokeDasharray="4 4" strokeWidth={1.5}
                  label={{ value: 'IMF Target 0.50%', position: 'insideTopRight', fontSize: 10, fill: '#d4a017' }} />
                <ReferenceLine y={0.35} stroke="#fbbf24" strokeDasharray="3 3" strokeWidth={1}
                  label={{ value: 'Regional Avg 0.35%', position: 'insideBottomRight', fontSize: 9, fill: '#fbbf24' }} />
                <Bar dataKey="value" name="Property Tax/GDP" radius={[4, 4, 0, 0]}>
                  {chartData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} fillOpacity={0.85} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {activeView === 'radar' && (
          <div className="rounded-xl p-4" style={{ background: 'var(--dark-elevated)', border: '1px solid var(--dark-border)' }}>
            <p className="text-xs font-semibold mb-3" style={{ color: 'var(--dark-text)' }}>Multi-Dimensional Performance Comparison (normalised 0–100)</p>
            <ResponsiveContainer width="100%" height={300}>
              <RadarChart data={RADAR_DATA} margin={{ top: 10, right: 30, left: 30, bottom: 10 }}>
                <PolarGrid stroke="rgba(255,255,255,0.08)" />
                <PolarAngleAxis dataKey="metric" tick={{ fontSize: 10, fill: 'var(--dark-muted)' }} />
                <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 9, fill: 'var(--dark-muted)' }} />
                <Radar name="Rwanda" dataKey="Rwanda" stroke="#4ade80" fill="#4ade80" fillOpacity={0.1} strokeWidth={2} />
                <Radar name="Kenya" dataKey="Kenya" stroke="#60a5fa" fill="#60a5fa" fillOpacity={0.08} strokeWidth={1.5} />
                <Radar name="Liberia (Now)" dataKey="Liberia" stroke="#f87171" fill="#f87171" fillOpacity={0.15} strokeWidth={2} strokeDasharray="4 4" />
                <Radar name="Liberia (Target)" dataKey="LiberiaTarget" stroke="#d4a017" fill="#d4a017" fillOpacity={0.12} strokeWidth={2} />
              </RadarChart>
            </ResponsiveContainer>
            <div className="flex flex-wrap gap-3 justify-center mt-2">
              {[
                { label: 'Rwanda', color: '#4ade80' },
                { label: 'Kenya', color: '#60a5fa' },
                { label: 'Liberia (Now)', color: '#f87171' },
                { label: 'Liberia (Target)', color: '#d4a017' },
              ].map(({ label, color }) => (
                <div key={label} className="flex items-center gap-1.5">
                  <div className="w-3 h-0.5 rounded" style={{ background: color }} />
                  <span className="text-xs" style={{ color: 'var(--dark-muted)' }}>{label}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeView === 'table' && (
          <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--dark-border)' }}>
            <table className="w-full text-xs">
              <thead>
                <tr style={{ background: 'var(--dark-elevated)', borderBottom: '1px solid var(--dark-border)' }}>
                  {['Country', 'Property Tax/GDP', 'Total Tax/GDP', 'Registration Rate', 'Digital System', 'Status', 'Notes'].map(h => (
                    <th key={h} className="px-3 py-2.5 text-left font-semibold" style={{ color: 'var(--dark-muted)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {BENCHMARK_DATA.map((row, i) => {
                  const style = STATUS_STYLE[row.status] ?? STATUS_STYLE['Developing'];
                  return (
                    <tr key={row.country}
                      style={{
                        borderBottom: i < BENCHMARK_DATA.length - 1 ? '1px solid var(--dark-border)' : 'none',
                        background: row.country.includes('Liberia') ? 'rgba(212,160,23,0.04)' : i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)',
                      }}>
                      <td className="px-3 py-2.5 font-semibold" style={{ color: 'var(--dark-text)' }}>
                        <span className="mr-1.5">{row.flag}</span>{row.country}
                      </td>
                      <td className="px-3 py-2.5 font-black text-sm" style={{ color: row.color }}>{row.propertyTaxGDP}%</td>
                      <td className="px-3 py-2.5" style={{ color: 'var(--dark-muted)' }}>{row.totalTaxGDP}%</td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-2">
                          <div className="w-12 h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)' }}>
                            <div className="h-full rounded-full" style={{ width: `${row.registrationRate}%`, background: row.color }} />
                          </div>
                          <span style={{ color: row.color }}>{row.registrationRate}%</span>
                        </div>
                      </td>
                      <td className="px-3 py-2.5">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full`}
                          style={{ background: row.digitalSystem ? 'rgba(74,222,128,0.12)' : 'rgba(239,68,68,0.1)', color: row.digitalSystem ? '#4ade80' : '#f87171' }}>
                          {row.digitalSystem ? '✓ Digital' : '✗ Manual'}
                        </span>
                      </td>
                      <td className="px-3 py-2.5">
                        <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ background: style.bg, color: style.color }}>
                          {row.status}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 max-w-xs" style={{ color: 'var(--dark-muted)' }}>{row.note}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Strategic insight */}
        <div className="mt-5 rounded-xl px-5 py-4" style={{ background: 'rgba(96,165,250,0.06)', border: '1px solid rgba(96,165,250,0.2)' }}>
          <div className="flex items-start gap-3">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" style={{ color: '#60a5fa' }} />
            <div>
              <p className="text-sm font-bold mb-1" style={{ color: '#60a5fa' }}>Strategic Framing for Decision-Makers</p>
              <p className="text-xs leading-relaxed" style={{ color: 'var(--dark-muted)' }}>
                Liberia's property tax at 0.09% of GDP is not just an administrative gap — it is a <span className="font-semibold" style={{ color: 'var(--dark-text)' }}>structural fiscal vulnerability</span> flagged in the IMF's 2023 Article IV Consultation.
                Rwanda achieved 0.62% through a digital cadastre system deployed in 2012. Kenya reached 0.48% through county-level digital valuation rolls.
                Both countries used the same approach LRA Nexus proposes: <span className="font-semibold" style={{ color: '#d4a017' }}>digital registration, GIS-backed valuation, and mobile-first field collection</span>.
                Closing even half the gap to the regional average would add $8M+ annually to Liberia's fiscal position.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
